import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Autocomplete } from '../shared/components'
import { useCarFipe } from '../hooks/useFipe'
import { vehicleService } from '../services/vehicle'

interface BatteryResult {
    model: string
    amperage: number
    image: string
    technology: string
    brand: string
    relevanceScore: number
    price?: number
    slug?: string
}

const VehicleSearchWidget: React.FC = () => {
    const navigate = useNavigate()
    const {
        brands,
        models,
        years,
        loading,
        selectedBrand,
        selectedModel,
        selectedYear,
        setSelectedBrand,
        setSelectedModel,
        setSelectedYear
    } = useCarFipe()

    const [results, setResults] = useState<BatteryResult[]>([])
    const [searching, setSearching] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({})

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(price)
    }

    const handleSearch = async () => {
        if (selectedBrand && selectedModel && selectedYear) {
            setSearching(true)
            setHasSearched(true)
            try {
                const batteries = await vehicleService.getCompatibleBatteries(
                    selectedBrand.nome,
                    selectedModel.nome,
                    selectedYear.nome
                )
                // Ordenar por relevância (relevanceScore) em ordem decrescente
                // e limitar a 3 baterias
                const sortedBatteries = batteries
                    .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))
                    .slice(0, 3)
                setResults(sortedBatteries)
                // Resetar erros de imagem quando novos resultados chegarem
                setImageErrors({})
            } catch (error) {
                console.error("Error searching battery:", error)
                setResults([])
            } finally {
                setSearching(false)
            }
        }
    }

    return (
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-6 md:p-8 relative z-20 w-full border border-blue-100">
            <div className="flex flex-col md:flex-row items-center justify-between mb-8">
                <div className="w-full md:w-auto">
                    <div className="inline-block bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-xs font-bold mb-4">
                        BUSCA INTELIGENTE
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        Qual a bateria do seu <span className="text-blue-600">veículo?</span>
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Selecione o veículo para encontrar a bateria compatível
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                {/* Marca */}
                <div className="flex flex-col">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Marca
                    </label>
                    <Autocomplete
                        value={selectedBrand?.codigo || ''}
                        onChange={(value) => {
                            const brand = brands.find(b => b.codigo === value)
                            setSelectedBrand(brand || null)
                            setResults([])
                            setHasSearched(false)
                        }}
                        options={brands.map(brand => ({
                            value: brand.codigo,
                            label: brand.nome
                        }))}
                        placeholder="Selecione..."
                        disabled={loading}
                        className="w-full"
                    />
                </div>

                {/* Modelo */}
                <div className="flex flex-col">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Modelo
                    </label>
                    <Autocomplete
                        value={selectedModel?.codigo || ''}
                        onChange={(value) => {
                            const model = models.find(m => m.codigo === value)
                            setSelectedModel(model || null)
                            setResults([])
                            setHasSearched(false)
                        }}
                        options={models.map(model => ({
                            value: model.codigo,
                            label: model.nome
                        }))}
                        placeholder="Selecione..."
                        disabled={!selectedBrand || loading}
                        className="w-full"
                    />
                </div>

                {/* Ano */}
                <div className="flex flex-col">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Ano
                    </label>
                    <Autocomplete
                        value={selectedYear?.codigo || ''}
                        onChange={(value) => {
                            const year = years.find(y => y.codigo === value)
                            setSelectedYear(year || null)
                            setResults([])
                            setHasSearched(false)
                        }}
                        options={years.map(year => ({
                            value: year.codigo,
                            label: year.nome
                        }))}
                        placeholder="Selecione..."
                        disabled={!selectedModel || loading}
                        className="w-full"
                    />
                </div>

                {/* Botão */}
                <div className="flex flex-col">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 opacity-0 pointer-events-none">
                        Buscar
                    </label>
                    <button
                        onClick={handleSearch}
                        disabled={!selectedBrand || !selectedModel || !selectedYear || loading || searching}
                        className="w-full bg-yellow-400 text-blue-900 font-bold py-2.5 px-4 rounded-lg transition-all duration-300 transform hover:scale-[1.02] shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center h-[42px]"
                    >
                        {searching ? (
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        )}
                        BUSCAR
                    </button>
                </div>
            </div>

            {hasSearched && (
                <div className="mt-8 border-t-2 border-blue-100 pt-8 animate-fade-in">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">
                            Baterias Recomendadas
                        </h3>
                    </div>

                    {results.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {results.map((result, index) => (
                                <div key={index} className="border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 bg-white hover:border-blue-300 transform hover:-translate-y-1 flex flex-col">
                                    <div className="flex justify-between items-start mb-3">
                                        <span className="bg-gradient-to-r from-green-400 to-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                                            {result.amperage}Ah
                                        </span>
                                        {index === 0 && (
                                            <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md flex items-center gap-1">
                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                                Mais Indicada
                                            </span>
                                        )}
                                    </div>

                                    <div 
                                        className="my-3 flex-grow flex items-center justify-center cursor-pointer"
                                        onClick={() => {
                                            if (result.slug) {
                                                navigate(`/produto/${result.slug}`)
                                            } else {
                                                navigate(`/baterias?amperage=${result.amperage}`)
                                            }
                                        }}
                                    >
                                        {!imageErrors[index] && result.image ? (
                                            <img
                                                src={result.image}
                                                alt={`Bateria ${result.model}`}
                                                className="h-24 object-contain hover:scale-105 transition-transform"
                                                onError={() => {
                                                    setImageErrors(prev => ({ ...prev, [index]: true }))
                                                }}
                                            />
                                        ) : (
                                            <div className="h-24 w-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                                                <svg
                                                    className="w-16 h-16 text-gray-300"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={1}
                                                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    <div className="text-center">
                                        <h4 
                                            className="font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                                            onClick={() => {
                                                if (result.slug) {
                                                    navigate(`/produto/${result.slug}`)
                                                } else {
                                                    navigate(`/baterias?amperage=${result.amperage}`)
                                                }
                                            }}
                                        >
                                            {result.model}
                                        </h4>
                                        <p className="text-sm text-gray-500 mb-2">{result.brand} - {result.technology}</p>
                                        
                                        {result.price && (
                                            <div className="mb-3">
                                                <div className="text-2xl font-bold text-blue-600">
                                                    {formatPrice(result.price)}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Ou 12x de {formatPrice(result.price / 12)}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            onClick={() => {
                                                if (result.slug) {
                                                    navigate(`/produto/${result.slug}`)
                                                } else {
                                                    navigate(`/baterias?amperage=${result.amperage}`)
                                                }
                                            }}
                                            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                                        >
                                            Ver Ofertas
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-6 bg-gray-50 rounded-xl">
                            <p className="text-gray-600">
                                Nenhuma bateria encontrada especificamente para este veículo na nossa base.
                            </p>
                            <button
                                onClick={() => navigate('/baterias')}
                                className="mt-4 text-blue-600 font-semibold hover:underline"
                            >
                                Ver catálogo completo
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default VehicleSearchWidget
